import React, { useState } from "react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { Send, Mail, MessageCircle, Github, Linkedin, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import { endpoints } from "../../lib/api";

export default function Contact({ config }) {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email and message."); return; }
        setLoading(true);
        try {
            await endpoints.contact(form);
            setSent(true);
            toast.success("Message sent! I'll get back to you soon.");
            setForm({ name: "", email: "", subject: "", message: "" });
            setTimeout(() => setSent(false), 4000);
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally { setLoading(false); }
    };

    const email = config?.email || "Hariompatel33699@gmail.com";
    const phone = config?.whatsapp || "+916307643369";
    const wa = phone.replace(/[^0-9]/g, "");
    const links = [
        { icon: Mail, label: "Email", value: email, href: `mailto:${email}`, id: "contact-email" },
        { icon: MessageCircle, label: "WhatsApp & Phone", value: phone, href: `https://wa.me/${wa}`, id: "contact-whatsapp" },
        { icon: Linkedin, label: "LinkedIn", value: "Connect", href: config?.socials?.linkedin || "https://www.linkedin.com/in/hariompatel069/", id: "contact-linkedin" },
        { icon: Github, label: "GitHub", value: "Follow", href: config?.socials?.github || "https://github.com/hariompatel61", id: "contact-github" },
    ];

    return (
        <section className="max-w-6xl mx-auto px-5 py-24 md:py-32">
            <SectionHeading id="contact" index="09 / Contact" subtitle="Get in touch" title="Have a role, project or idea? Let's talk." />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <Reveal className="lg:col-span-3">
                    <form onSubmit={submit} className="glass rounded-3xl p-7" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Name" testid="contact-name"><input value={form.name} onChange={set("name")} data-testid="contact-name" className="input" placeholder="Jane Doe" /></Field>
                            <Field label="Email" testid="contact-email-input"><input type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className="input" placeholder="jane@company.com" /></Field>
                        </div>
                        <div className="mt-4"><Field label="Subject"><input value={form.subject} onChange={set("subject")} data-testid="contact-subject" className="input" placeholder="Opportunity / Project / Hello" /></Field></div>
                        <div className="mt-4"><Field label="Message"><textarea value={form.message} onChange={set("message")} data-testid="contact-message" rows={5} className="input resize-none" placeholder="Tell me a bit about it…" /></Field></div>
                        <button type="submit" disabled={loading} data-testid="contact-submit"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-foreground text-background rounded-xl py-3 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                            {loading ? "Sending…" : sent ? "Sent!" : "Send Message"}
                        </button>
                    </form>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-2">
                    <div className="flex flex-col gap-4 h-full">
                        <div className="glass rounded-3xl p-6">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></span>
                                <span className="font-mono text-xs uppercase tracking-widest text-green-500">Available</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{config?.availability}</p>
                        </div>
                        {links.map((l) => (
                            <a key={l.id} href={l.href} target="_blank" rel="noreferrer" data-testid={l.id}
                                className="group glass rounded-2xl p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform">
                                <span className="w-10 h-10 grid place-items-center rounded-xl bg-secondary text-primary group-hover:scale-110 transition-transform"><l.icon className="w-4 h-4" /></span>
                                <div><div className="text-xs text-muted-foreground">{l.label}</div><div className="text-sm font-medium truncate">{l.value}</div></div>
                            </a>
                        ))}
                        <a href={config?.resume_url || "/Resume.pdf"} download="Hariom_Patel_Resume.pdf" target="_blank" rel="noreferrer" data-testid="contact-resume"
                            className="glass rounded-2xl p-4 flex items-center gap-3 hover:text-primary transition-colors">
                            <FileDown className="w-4 h-4" /> <span className="text-sm font-medium">Download Résumé</span>
                        </a>
                    </div>
                </Reveal>
            </div>
            <style>{`.input{width:100%;background:hsl(var(--secondary));border:1px solid hsl(var(--border));border-radius:0.75rem;padding:0.7rem 0.9rem;font-size:0.875rem;outline:none;transition:border .2s}.input:focus{border-color:hsl(var(--primary))}`}</style>
        </section>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>
        {children}
    </label>
);
